import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { array, string, bool, number, func } from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { GLOBAL } from '../../Config/global';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles.scss';
import ConfirmationModal from '../ConfirmationModal';
import CellInput from './components/CellInput';
import { ResizeHandle } from './components';
import Tooltip from '../Tooltip';
import Loading from '../Loading';

const renderSpan = (spanClass) => (
  <span className={spanClass} />
);

const TextProps = {
  previousText: renderSpan('before-btn'),
  nextText: renderSpan('after-btn'),
  ofText: GLOBAL.TABLE_VIEW.OF_TEXT,
  rowsText: GLOBAL.TABLE_VIEW.ROWS_TEXT,
  pageText: GLOBAL.TABLE_VIEW.PAGE_TEXT
};

const Table = ({
  navigate,
  columns,
  className = "",
  data,
  defaultPageSize = 10,
  filterable = true,
  onSortedChange,
  onPageSizeChange,
  changePage,
  loading = false,
  filter,
  sorted = [],
  manual = false,
  onKeyDown,
  pages,
  page,
  onFilterChange,
  pageSize = 10,
  expandable = false,
  subComponent = null,
  resetColumnFilters = false,
  isChildTable = false,
  hidePagination = false,
  hideFooter = false,
  getRowProps: customGetRowProps }) => {
  const [selection, setSelection] = useState([]);
  const [selectAll] = useState(false);
  const [editingRow] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [internalPageSize, setInternalPageSize] = useState(pageSize || defaultPageSize || 10);
  const [internalPageIndex, setInternalPageIndex] = useState(page || 0);
  const [internalSorting, setInternalSorting] = useState(sorted || []);
  const [internalFilters, setInternalFilters] = useState(filter || []);
  const [columnFilters, setColumnFilters] = useState({});
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);

  // Initialize column widths from column definitions
  useEffect(() => {
    const initialWidths = {};
    columns.forEach(col => {
      if (col.width) {
        // Try multiple possible column identifiers
        const columnId = col.id || col.accessor || col.field || col.name;
        if (columnId) {
          initialWidths[columnId] = col.width;
        }
      }
    });
    if (Object.keys(initialWidths).length > 0) {
      setColumnWidths(initialWidths);
    }
  }, [columns]);

  // Reset column filters when resetColumnFilters prop changes to true
  useEffect(() => {
    if (resetColumnFilters) {
      setColumnFilters({});
    }
  }, [resetColumnFilters]);

  // Refs for debounce
  const filterTimeoutRefs = useRef({});

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const toggleExportModal = () => {
    setShowExportModal(!showExportModal);
  };

  const onSuccessRedirect = () => {
    navigate('/table-success');
  };

  const toggleAllSelection = () => {
    if (selectAll) {
      setSelection(data.map(record => `select-${record.id}`));
    } else {
      setSelection([]);
    }
  };

  const isSelected = key => selection.includes(`select-${key}`);

  const isEditing = key => editingRow && editingRow.index === key;

  const getSelectedRowProps = rowInfo => (rowInfo && isSelected(rowInfo.original.id)) ? "-selected" : (rowInfo && selection.length && !isSelected(rowInfo.original.id) ? '-disabled' : '');

  const getEditingRowProps = rowInfo => rowInfo && isEditing(rowInfo.index) ? "-editing" : rowInfo && editingRow && !isEditing(rowInfo.index) ? '-disabled' : '';

  useEffect(() => {
    toggleAllSelection();
  }, [selectAll]);

  const memoizedColumns = useMemo(() => {
    let processedColumns = columns.map(col => {
      let accessorKey;
      let columnId;

      if (typeof col.id === 'string') {
        accessorKey = col.id;
        columnId = col.id;
      } else if (typeof col.accessor === 'string') {
        accessorKey = col.accessor;
        columnId = col.accessor;
      } else if (typeof col.field === 'string') {
        accessorKey = col.field;
        columnId = col.field;
      } else if (typeof col.name === 'string') {
        accessorKey = col.name;
        columnId = col.name;
      }

      return {
        ...col,
        id: columnId || accessorKey, // Ensure we have a consistent ID
        header: col.Header,
        accessorKey: accessorKey,
        cell: col.Cell ? (props) => col.Cell({ ...props, value: props.getValue(), original: props.row.original }) : (props) => props.getValue(),
        footer: col.Footer ? (context) => {
          const rowData = table.getRowModel().rows.map(row => row.original);
          return col.Footer({ ...context, data: rowData });
        } : undefined,
        enableSorting: col.sortable !== false,
        enableColumnFilter: col.filterable !== false,
        headerStyle: col.headerStyle || {},
        style: col.style || {},
      };
    }).filter(col => col.accessorKey);

    // Add expander column if expandable
    if (expandable) {
      const expanderColumn = {
        id: 'expander',
        header: () => null,
        accessorKey: 'expander',
        width: 35,
        enableSorting: false,
        enableColumnFilter: false,
        headerStyle: { width: '35px', minWidth: '35px', maxWidth: '35px', padding: '0' },
        style: { width: '35px', minWidth: '35px', maxWidth: '35px', padding: '0', textAlign: 'center' },
        cell: ({ row }) => (
          <Tooltip
            text={row.getIsExpanded() ? 'Contraer fila' : 'Expandir fila'}
            placement="right"
          >
            <span
              style={{
                cursor: 'pointer',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                transform: row.getIsExpanded() ? 'rotate(90deg)' : 'rotate(0deg)',
                fontSize: '12px',
                fontWeight: 'bold',
                color: row.getIsExpanded() ? '#0066cc' : '#666',
                padding: '4px',
                borderRadius: '3px',
                backgroundColor: 'transparent',
                userSelect: 'none'
              }}
              onClick={() => row.toggleExpanded()}
              onMouseEnter={(e) => {
                e.target.style.transform = row.getIsExpanded() ? 'rotate(90deg) scale(1.2)' : 'rotate(0deg) scale(1.2)';
                e.target.style.color = '#0066cc';
                e.target.style.backgroundColor = '#f0f8ff';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 102, 204, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = row.getIsExpanded() ? 'rotate(90deg)' : 'rotate(0deg)';
                e.target.style.color = row.getIsExpanded() ? '#0066cc' : '#666';
                e.target.style.backgroundColor = 'transparent';
                e.target.style.boxShadow = 'none';
              }}
              onMouseDown={(e) => {
                e.target.style.transform = row.getIsExpanded() ? 'rotate(90deg) scale(0.95)' : 'rotate(0deg) scale(0.95)';
              }}
              onMouseUp={(e) => {
                e.target.style.transform = row.getIsExpanded() ? 'rotate(90deg) scale(1.2)' : 'rotate(0deg) scale(1.2)';
              }}
            >
              ▶
            </span>
          </Tooltip>
        ),
      };

      processedColumns = [expanderColumn, ...processedColumns];
    }

    return processedColumns;
  }, [columns, expandable, data]);

  const memoizedColumnFilters = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([_, value]) => value && value.trim() !== '')
      .map(([id, value]) => ({ id, value: value.toLowerCase() }));
  }, [columnFilters]);

  const table = useReactTable({
    data: data || [],
    columns: memoizedColumns,
    state: {
      sorting: manual ? sorted : internalSorting,
      pagination: {
        pageIndex: manual ? page : internalPageIndex,
        pageSize: manual ? pageSize : internalPageSize,
      },
      globalFilter: manual ? filter : internalFilters,
      columnFilters: memoizedColumnFilters,
    },
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(internalSorting) : updater;
      if (manual && onSortedChange) {
        onSortedChange(newSorting);
      } else {
        setInternalSorting(newSorting);
      }
    },
    onPaginationChange: (updater) => {
      const currentState = manual ? {
        pageIndex: page || 0,
        pageSize: pageSize || defaultPageSize || 10,
      } : {
        pageIndex: internalPageIndex,
        pageSize: internalPageSize,
      };

      const newState = typeof updater === 'function' ? updater(currentState) : updater;

      if (manual && changePage) {
        changePage(newState.pageIndex);
      } else {
        setInternalPageIndex(newState.pageIndex);
        setInternalPageSize(newState.pageSize);
      }
    },
    onGlobalFilterChange: (updater) => {
      const newFilter = typeof updater === 'function' ? updater(internalFilters) : updater;
      if (manual && onFilterChange) {
        onFilterChange(newFilter);
      } else {
        setInternalFilters(newFilter);
      }
    },
    onColumnFiltersChange: (updater) => {
      // This is handled by our custom handleColumnFilterChange function
      // We don't need to do anything here since we manage columnFilters state manually
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: manual,
    manualSorting: manual,
    manualFiltering: manual,
    pageCount: manual ? pages : undefined,
  });

  // Scroll control
  let refDivTable = useRef();
  let refDivScroll1 = useRef();
  let refDivScroll2 = useRef();

  useEffect(() => {
    if (refDivTable.current) {
      setWidthDivScroll();
    }
  });

  useEffect(() => {
    setTopDivScroll();
  });

  const scrollHorizontalTable = () => {
    setWidthDivScroll();
    if (refDivTable.current && refDivScroll1.current) {
      refDivTable.current.scrollLeft = refDivScroll1.current.scrollLeft;
    }
  };

  const calculateWidthDivScroll = () => {
    try {
      return (refDivTable.current && refDivTable.current.scrollWidth) ? refDivTable.current.scrollWidth : window.innerWidth + 10;
    } catch {
      return window.innerWidth + 10;
    }
  };

  const calculateTopDivScroll = () => {
    if (isChildTable) {
      return 1000;
    }
    return window.innerHeight - 20;
  };

  const setWidthDivScroll = () => {
    if (refDivScroll2.current) {
      refDivScroll2.current.style.width = calculateWidthDivScroll().toString() + "px";
    }
  };

  const setTopDivScroll = () => {
    if (refDivScroll1.current) {
      refDivScroll1.current.style.top = calculateTopDivScroll().toString() + "px";
    }
  };

  const buildRowProps = (row) => {
    const rowInfo = { original: row.original, index: row.index };
    const customProps = customGetRowProps ? customGetRowProps(rowInfo) || {} : {};
    const baseClassName = `${getSelectedRowProps(rowInfo)} ${getEditingRowProps(rowInfo)}`.trim();
    const customClassName = customProps.className || '';

    return {
      ...customProps,
      className: `${baseClassName} ${customClassName}`.trim(),
      style: {
        ...(customProps.style || {})
      }
    };
  };

  // Apply filters to table (debounced)
  const applyFilters = useCallback((filters) => {
    const filterArray = Object.entries(filters)
      .filter(([_, value]) => value && value.trim() !== '')
      .map(([id, value]) => ({ id, value: value.toLowerCase() }));

    if (manual && onFilterChange) {
      onFilterChange(filterArray);
    } else {
      setColumnFilters(filters);
    }
  }, [manual, onFilterChange, table]);

  // Handle column filter change with debounce
  const handleColumnFilterChange = (columnId, value) => {
    // Update input value immediately
    const newFilters = { ...columnFilters, [columnId]: value };
    setColumnFilters(newFilters);

    // Clear existing timeout for this column
    if (filterTimeoutRefs.current[columnId]) {
      clearTimeout(filterTimeoutRefs.current[columnId]);
    }

    // Set new timeout to apply filter after delay
    filterTimeoutRefs.current[columnId] = setTimeout(() => {
      applyFilters(newFilters);
      delete filterTimeoutRefs.current[columnId];
    }, 500); // 500ms delay
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(filterTimeoutRefs.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // Functions to handle column resizing
  const handleColumnResize = useCallback((columnId, width) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: width
    }));
  }, []);

  const handleColumnResizeEnd = useCallback(() => {
    setResizingColumn(null);
  }, []);

  // Get column width with fallback to default
  const getColumnWidth = useCallback((columnId) => {
    if (columnId === 'expander' && expandable) {
      return '35px';
    }
    return columnWidths[columnId] || null;
  }, [columnWidths, expandable]);

  return (
    <React.Fragment>
      {showDeleteModal && <ConfirmationModal
        message={GLOBAL.TABLE_VIEW.DELETE_MODAL_BODY}
        status="warning"
        title={GLOBAL.TABLE_VIEW.DELETE_MODAL_TITLE}
        onDismiss={toggleDeleteModal}
        onConfirm={onSuccessRedirect}
      />}
      {showExportModal && <ConfirmationModal
        message={GLOBAL.TABLE_VIEW.EXPORT_MODAL_BODY}
        title={GLOBAL.TABLE_VIEW.EXPORT_MODAL_TITLE}
        status="primary"
        onDismiss={toggleExportModal}
        onConfirm={onSuccessRedirect}
      />}

      {!isChildTable && (
        <div
          ref={refDivScroll1}
          onScroll={scrollHorizontalTable}
          style={{
            overflow: "auto",
            position: "sticky",
            zIndex: "2",
            top: calculateTopDivScroll().toString() + "px"
          }}
        >
          <div
            ref={refDivScroll2}
            style={{
              width: calculateWidthDivScroll().toString() + "px",
              height: "1px"
            }}
          />
        </div>
      )}

      <div
        ref={refDivTable}
        style={{
          overflowX: isChildTable ? "auto" : "hidden",
          overflowY: "hidden",
          marginBottom: isChildTable ? "0" : "20px",
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          position: "relative"
        }}
      >
        <Loading visible={loading} />
        <div className={`react-table -striped -highlight ${className} ${resizingColumn ? 'table-resizing' : ''}`} style={{ opacity: loading ? 0.5 : 1 }}>
          <table>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <React.Fragment key={headerGroup.id}>
                  <tr>
                    {headerGroup.headers.map(header => (
                      <Tooltip
                        text={header.column.columnDef.description || (typeof header.column.columnDef.header === 'function' ? '' : header.column.columnDef.header)}
                        placement="top"
                        disabled={!header.column.columnDef.description && (!header.column.columnDef.header || typeof header.column.columnDef.header === 'function')}
                      >
                        <th
                          key={header.id}
                          style={{
                            ...header.column.columnDef.headerStyle,
                            cursor: 'default',
                            width: getColumnWidth(header.id),
                            minWidth: getColumnWidth(header.id),
                            maxWidth: getColumnWidth(header.id),
                            position: 'relative',
                            zIndex: header.index + 1
                          }}
                          className={`${header.column.getCanSort() ? `${header.column.getIsSorted() === 'asc' ? '-sort-asc' : header.column.getIsSorted() === 'desc' ? '-sort-desc' : ''}` : ''} ${header.id !== 'expander' ? 'resizable' : ''} ${header.id === 'expander' ? 'expander-column' : ''}`}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            paddingRight: header.column.getCanSort() ? '25px' : '8px'
                          }}>
                            <span>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            </span>
                            {header.column.getCanSort() && (
                              <i
                                className={`bi ${header.column.getIsSorted() === 'asc' ? 'bi-arrow-down' : header.column.getIsSorted() === 'desc' ? 'bi-arrow-up' : 'bi-arrow-down-up'}`}
                                onClick={header.column.getToggleSortingHandler()}
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '8px',
                                  fontSize: '13px',
                                  color: header.column.getIsSorted() ? '#0066cc' : '#666',
                                  transition: 'all 0.2s ease',
                                  padding: '2px',
                                  borderRadius: '3px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.color = '#0066cc';
                                  e.target.style.backgroundColor = 'rgba(0, 102, 204, 0.1)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.color = header.column.getIsSorted() ? '#0066cc' : '#666';
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              />
                            )}
                          </div>
                          {header.id !== 'expander' && (
                            <ResizeHandle
                              onResize={(width) => handleColumnResize(header.id, width)}
                              onResizeEnd={() => handleColumnResizeEnd()}
                              minWidth={50}
                              maxWidth={500}
                            />
                          )}
                        </th>
                      </Tooltip>
                    ))}
                  </tr>
                  {filterable && (
                    <tr>
                      {headerGroup.headers.map(header => (
                        <th
                          key={`${header.id}-filter`}
                          style={{
                            ...header.column.columnDef.headerStyle,
                            padding: '8px 10px',
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            width: getColumnWidth(header.id),
                            minWidth: getColumnWidth(header.id),
                            maxWidth: getColumnWidth(header.id),
                            zIndex: header.index + 1
                          }}
                        >
                          {header.column.columnDef.enableColumnFilter !== false && (
                            <CellInput

                              onChange={(e) => handleColumnFilterChange(header.id, e.target.value)}
                              // onChange={(e) => console.log(e, header.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <React.Fragment key={row.id}>
                  <tr {...buildRowProps(row)}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        style={{
                          ...cell.column.columnDef.style,
                          width: getColumnWidth(cell.column.id),
                          minWidth: getColumnWidth(cell.column.id),
                          maxWidth: getColumnWidth(cell.column.id),
                          zIndex: (cell.column.index || 0) + 1
                        }}
                        className={cell.column.id === 'expander' ? 'expander-column' : ''}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {expandable && subComponent && row.getIsExpanded() && (
                    <tr>
                      <td colSpan={memoizedColumns.length}>
                        {subComponent({ original: row.original })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {!hideFooter && (
              <tfoot>
                {table.getFooterGroups().map(footerGroup => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map(header => (
                      <td
                        key={header.id}
                        style={{
                          ...header.column.columnDef.style,
                          width: getColumnWidth(header.id),
                          minWidth: getColumnWidth(header.id),
                          maxWidth: getColumnWidth(header.id),
                          fontWeight: 'bold',
                          backgroundColor: '#f8f9fa',
                          borderTop: '2px solid #dee2e6'
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>

          {/* Pagination */}
          {!hidePagination && (
            <div className="pagination-bottom">
              <div className="-pagination">
              <div className="-previous">
                <Tooltip
                  text="Página anterior"
                  placement="top"
                  disabled={!table.getCanPreviousPage()}
                >
                  <button
                    className="-btn"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    {TextProps.previousText}
                  </button>
                </Tooltip>
              </div>
              <div className="-center">
                <span className="-pageInfo">
                  {TextProps.pageText}{' '}
                  <strong>
                    {table.getState().pagination.pageIndex + 1} {TextProps.ofText} {table.getPageCount()}
                  </strong>{' '}
                </span>
                <select
                  className="-pageJump"
                  value={table.getState().pagination.pageSize}
                  onChange={e => {
                    const newPageSize = Number(e.target.value);
                    if (manual && onPageSizeChange) {
                      onPageSizeChange(newPageSize);
                    } else {
                      table.setPageSize(newPageSize);
                    }
                  }}
                >
                  {[10, 20, 30, 40, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize} {TextProps.rowsText}
                    </option>
                  ))}
                </select>
              </div>
              <div className="-next">
                <Tooltip
                  text="Página siguiente"
                  placement="top"
                  disabled={!table.getCanNextPage()}
                >
                  <button
                    className="-btn"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    {TextProps.nextText}
                  </button>
                </Tooltip>
              </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

Table.propTypes = {
  className: string,
  columns: array.isRequired,
  filterable: bool,
  defaultPageSize: number,
  data: array.isRequired,
  keyField: string,
  resizable: bool,
  getTableRef: func,
  onPageSizeChange: func,
  defaultSortingMethod: func,
  handleToggleAllSelection: func,
  handleToggleSingleSelection: func,
  handleSetSelectAll: func,
  getRowProps: func,
  selectInputComponent: func,
  selectAll: bool,
  isSelected: func,
  manual: bool,
  pageSize: number,
  onFilterChange: func,
  onSortedChange: func,
  loading: bool,
  pages: number,
  page: number,
  expandable: bool,
  subComponent: func,
  resetColumnFilters: bool,
  isChildTable: bool,
  hidePagination: bool,
  hideFooter: bool
};


export default Table;
